import { useState, useEffect, useRef } from 'react';
import type { ReactNode, FocusEvent, KeyboardEvent } from 'react';
import type { WidgetTheme } from '../types';
import { isAlternateTheme } from '../utils/theme';
import styles from '../styles/widget.module.scss';

interface PopupProps {
  trigger: ReactNode;
  children: ReactNode;
  direction?: 'top' | 'bottom' | 'left' | 'right' | 'hidden';
  /**
   * Whether the popup content (always `MetricsPanel` for this widget's two
   * Popup callers) renders its own colored header bar at the top — true
   * unless `theme` is an alternate theme that hides it. Drives the
   * 'bottom'-direction arrow's fill color so it matches whatever content
   * it actually touches, instead of always assuming a plain background.
   */
  theme?: WidgetTheme;
}

const Popup = ({ trigger, children, direction = 'top', theme }: PopupProps) => {
  const hasColoredHeader = !isAlternateTheme(theme);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(v => !v);
    }
  };

  const scheduleClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  const cancelClose = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
  };

  const handleWrapperBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;
    if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
      setOpen(false);
    }
  };

  if (direction === 'hidden') return <>{trigger}</>;

  return (
    <div
      ref={wrapperRef}
      className={styles.popupWrapper}
      data-direction={direction}
      data-panel-header={hasColoredHeader}
      onBlur={handleWrapperBlur}
    >
      <button
        type="button"
        className={styles.popupTrigger}
        aria-haspopup="true"
        aria-expanded={open}
        onMouseEnter={() => {
          cancelClose();
          setOpen(true);
        }}
        onMouseLeave={scheduleClose}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
      >
        {trigger}
      </button>
      {open && (
        <div
          className={styles.popupPanel}
          role="tooltip"
          tabIndex={-1}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default Popup;
